import { useState, useEffect, useCallback } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, Globe, Lock, Unlock,
  RefreshCw, Wrench, AlertTriangle, CheckCircle2, XCircle, Search,
  FileText, Map, Server, Zap, ChevronDown, ChevronUp, Clock,
  Activity, BarChart3, Download, Filter, ArrowUpDown, Eye,
  PlayCircle, PauseCircle, Terminal, Trash2, RotateCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';

/* ============ HELPERS ============ */
const scoreColor = (s) => {
  if (s >= 90) return 'text-emerald-400';
  if (s >= 70) return 'text-amber-400';
  if (s >= 50) return 'text-orange-400';
  return 'text-red-400';
};
const scoreBg = (s) => {
  if (s >= 90) return 'bg-emerald-500/15 border-emerald-500/30';
  if (s >= 70) return 'bg-amber-500/15 border-amber-500/30';
  if (s >= 50) return 'bg-orange-500/15 border-orange-500/30';
  return 'bg-red-500/15 border-red-500/30';
};
const statusIcon = (ok) => ok
  ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
  : <XCircle className="w-4 h-4 text-red-400" />;
const StatusDot = ({ ok, warning }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${
    ok ? 'bg-emerald-400' : warning ? 'bg-amber-400' : 'bg-red-400'
  }`} />
);

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return d; }
};

/* ============ MAIN COMPONENT ============ */
export default function ProductionHealthPage() {
  const [domains, setDomains] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('score');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [logFilter, setLogFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('health');

  /* ---- DATA LOADING ---- */
  const loadHealth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/domain-health');
      setDomains(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Health load error:', err);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const params = {};
      if (logFilter !== 'all') params.action = logFilter;
      const [logsRes, statsRes] = await Promise.all([
        api.get('/admin/repair-logs', { params: { ...params, limit: 100 } }),
        api.get('/admin/repair-logs/stats'),
      ]);
      setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      setLogStats(statsRes.data);
    } catch (err) {
      console.error('Logs load error:', err);
    }
  }, [logFilter]);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { if (activeTab === 'logs') loadLogs(); }, [activeTab, loadLogs]);

  /* ---- BULK ACTIONS ---- */
  const scanAll = async () => {
    setScanning(true);
    toast.info('Tum domainler taraniyor...');
    try {
      const res = await api.post('/admin/domain-health/scan-all');
      const data = res.data;
      setDomains(data.results || []);
      toast.success(`Tarama tamamlandi: ${data.ok}/${data.total} saglam (Ort. skor: ${data.avg_score})`);
    } catch (err) {
      toast.error('Tarama hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScanning(false);
    }
  };

  const confirmAction = (title, description, action) => {
    setConfirmDialog({ title, description, action });
  };

  const executeConfirmedAction = async () => {
    if (!confirmDialog) return;
    setConfirmDialog(null);
    await confirmDialog.action();
  };

  const runNginxTest = async () => {
    setActionLoading(p => ({ ...p, nginx_test: true }));
    try {
      const res = await api.post('/admin/nginx/test');
      if (res.data.success) {
        toast.success('Nginx config test basarili');
      } else {
        toast.error('Nginx config test basarisiz: ' + (res.data.output || '').slice(0, 200));
      }
    } catch (err) {
      toast.error('Nginx test hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(p => ({ ...p, nginx_test: false }));
    }
  };

  const runNginxReload = async () => {
    confirmAction(
      'Nginx Reload',
      'Nginx yapilandirmasi yeniden yuklenecek. Sadece nginx -t basariliysa islem yapilir.',
      async () => {
        setActionLoading(p => ({ ...p, nginx_reload: true }));
        try {
          const res = await api.post('/admin/nginx/reload');
          if (res.data.success) {
            toast.success('Nginx basariyla yeniden yuklendi');
          } else {
            toast.error('Nginx reload engellendi: ' + (res.data.detail || res.data.message || '').slice(0, 200));
          }
        } catch (err) {
          toast.error('Nginx reload hatasi: ' + (err.response?.data?.detail || err.message));
        } finally {
          setActionLoading(p => ({ ...p, nginx_reload: false }));
        }
      }
    );
  };

  const runQuarantine = async () => {
    confirmAction(
      'Bozuk Config Karantina',
      'Eksik SSL sertifika dosyalarina referans veren nginx config dosyalari disabled-sites dizinine tasinacak.',
      async () => {
        setActionLoading(p => ({ ...p, quarantine: true }));
        try {
          const res = await api.post('/admin/nginx/quarantine-broken-configs');
          toast.success(`Karantina tamamlandi: ${res.data.count || 0} config izole edildi`);
          loadHealth();
        } catch (err) {
          toast.error('Karantina hatasi: ' + (err.response?.data?.detail || err.message));
        } finally {
          setActionLoading(p => ({ ...p, quarantine: false }));
        }
      }
    );
  };

  const runBulkAutoFix = async () => {
    confirmAction(
      'Toplu Otomatik Onarim',
      'Tum domainler icin: karantina + HTTP config duzeltme + SSL kurulum calistirilacak. Bu islem birden fazla domain uzerinde degisiklik yapacak.',
      async () => {
        setActionLoading(p => ({ ...p, bulk_fix: true }));
        toast.info('Toplu onarim baslatildi...');
        try {
          const res = await api.post('/admin/domain-health/bulk-auto-fix');
          const d = res.data;
          toast.success(`Toplu onarim: ${d.ok}/${d.total_domains} basarili, ${d.dns_missing} DNS eksik, ${d.quarantined} karantina`);
          loadHealth();
        } catch (err) {
          toast.error('Toplu onarim hatasi: ' + (err.response?.data?.detail || err.message));
        } finally {
          setActionLoading(p => ({ ...p, bulk_fix: false }));
        }
      }
    );
  };

  /* ---- SINGLE DOMAIN ACTIONS ---- */
  const fixHttpConfig = async (domain) => {
    confirmAction(
      `HTTP Config Duzelt: ${domain}`,
      `${domain} icin temiz HTTP nginx config olusturulacak ve etkinlestirilecek.`,
      async () => {
        setActionLoading(p => ({ ...p, [`fix_${domain}`]: true }));
        try {
          const res = await api.post(`/admin/domain-health/${domain}/fix-http-config`);
          if (res.data.success) {
            toast.success(`${domain} HTTP config duzeltildi`);
            loadHealth();
          } else {
            toast.error(`HTTP config hatasi: ${res.data.error || 'bilinmeyen'}`);
          }
        } catch (err) {
          toast.error('Hata: ' + (err.response?.data?.detail || err.message));
        } finally {
          setActionLoading(p => ({ ...p, [`fix_${domain}`]: false }));
        }
      }
    );
  };

  const installSsl = async (domain) => {
    confirmAction(
      `SSL Kur: ${domain}`,
      `${domain} icin certbot ile SSL sertifikasi kurulacak. DNS A record dogru olmalidir.`,
      async () => {
        setActionLoading(p => ({ ...p, [`ssl_${domain}`]: true }));
        try {
          const res = await api.post(`/admin/domain-health/${domain}/install-ssl`);
          if (res.data.success) {
            toast.success(`${domain} SSL basariyla kuruldu`);
            loadHealth();
          } else {
            toast.error(`SSL hatasi: ${res.data.error} — ${(res.data.detail || '').slice(0, 150)}`);
          }
        } catch (err) {
          toast.error('Hata: ' + (err.response?.data?.detail || err.message));
        } finally {
          setActionLoading(p => ({ ...p, [`ssl_${domain}`]: false }));
        }
      }
    );
  };

  /* ---- SORTING + FILTERING ---- */
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filteredDomains = domains
    .filter(d => !searchTerm || d.domain?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'score') {
        va = a.score ?? 0; vb = b.score ?? 0;
      }
      if (sortField === 'domain') {
        va = a.domain || ''; vb = b.domain || '';
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });

  /* ---- KPI CALCULATIONS ---- */
  const totalDomains = domains.length;
  const readyCount = domains.filter(d => d.status === 'READY').length;
  const avgScore = totalDomains > 0 ? Math.round(domains.reduce((s, d) => s + (d.score || 0), 0) / totalDomains) : 0;
  const criticalCount = domains.reduce((s, d) => s + (d.critical_count || 0), 0);
  const warningCount = domains.reduce((s, d) => s + (d.warning_count || 0), 0);
  const sslExpiringCount = domains.filter(d => (d.ssl_expiry_days || 999) <= 15 && d.ssl_expiry_days >= 0).length;

  /* ---- RENDER ---- */
  return (
    <TooltipProvider>
      <div data-testid="production-health-page" className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 data-testid="production-health-title" className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Space_Grotesk']">
              Produksiyon Saglik Merkezi
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Nginx, SSL, DNS ve SEO dosyalarini tek panelden yonet
            </p>
          </div>
          <Button
            data-testid="scan-all-btn"
            onClick={scanAll}
            disabled={scanning}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
          >
            {scanning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {scanning ? 'Taraniyor...' : 'Tumunu Tara'}
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          <KPICard testId="kpi-total" label="Toplam Domain" value={totalDomains} icon={Globe} />
          <KPICard testId="kpi-ready" label="Saglam" value={readyCount} icon={ShieldCheck} color="text-emerald-400" />
          <KPICard testId="kpi-score" label="Ort. Skor" value={avgScore} icon={BarChart3} color={scoreColor(avgScore)} />
          <KPICard testId="kpi-critical" label="Kritik" value={criticalCount} icon={ShieldX} color="text-red-400" />
          <KPICard testId="kpi-warning" label="Uyari" value={warningCount} icon={AlertTriangle} color="text-amber-400" />
          <KPICard testId="kpi-ssl-expiring" label="SSL Bitiyor" value={sslExpiringCount} icon={Lock} color="text-orange-400" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="health" data-testid="tab-health" className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-[hsl(var(--primary-foreground))]">
              <Shield className="w-4 h-4 mr-2" /> Saglik Tablosu
            </TabsTrigger>
            <TabsTrigger value="actions" data-testid="tab-actions" className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-[hsl(var(--primary-foreground))]">
              <Wrench className="w-4 h-4 mr-2" /> Toplu Islemler
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-[hsl(var(--primary-foreground))]">
              <Terminal className="w-4 h-4 mr-2" /> Islem Gecmisi
            </TabsTrigger>
          </TabsList>

          {/* ======= TAB: HEALTH TABLE ======= */}
          <TabsContent value="health" className="space-y-4">
            {/* Search + Sort */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="domain-search"
                  placeholder="Domain ara..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 bg-secondary/30 border-border/50"
                />
              </div>
              <Select value={sortField} onValueChange={v => { setSortField(v); setSortDir('asc'); }}>
                <SelectTrigger data-testid="sort-select" className="w-[180px] bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Siralama" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Skor</SelectItem>
                  <SelectItem value="domain">Domain</SelectItem>
                  <SelectItem value="ssl_expiry_days">SSL Bitis</SelectItem>
                  <SelectItem value="http_status">HTTP Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
              </div>
            ) : filteredDomains.length === 0 ? (
              <Card className="bg-[hsl(var(--card))] border-border/70">
                <CardContent className="py-16 text-center">
                  <Shield className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
                  <p className="text-muted-foreground">
                    {domains.length === 0
                      ? 'Henuz tarama yapilmadi. "Tumunu Tara" ile baslat.'
                      : 'Aramayla eslesen domain bulunamadi.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[hsl(var(--card))] border-border/70 overflow-hidden">
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <SortableHeader field="domain" label="Domain" current={sortField} dir={sortDir} onSort={toggleSort} />
                        <TableHead className="text-xs text-muted-foreground">DNS</TableHead>
                        <TableHead className="text-xs text-muted-foreground">HTTP</TableHead>
                        <TableHead className="text-xs text-muted-foreground">HTTPS</TableHead>
                        <TableHead className="text-xs text-muted-foreground">SSL</TableHead>
                        <SortableHeader field="ssl_expiry_days" label="SSL Bitis" current={sortField} dir={sortDir} onSort={toggleSort} />
                        <TableHead className="text-xs text-muted-foreground">Nginx</TableHead>
                        <TableHead className="text-xs text-muted-foreground">Root</TableHead>
                        <TableHead className="text-xs text-muted-foreground">Sitemap</TableHead>
                        <TableHead className="text-xs text-muted-foreground">Robots</TableHead>
                        <SortableHeader field="score" label="Skor" current={sortField} dir={sortDir} onSort={toggleSort} />
                        <TableHead className="text-xs text-muted-foreground text-right">Islemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDomains.map(d => (
                        <DomainRow
                          key={d.domain}
                          data={d}
                          actionLoading={actionLoading}
                          onDetail={() => { setSelectedDomain(d); setDetailOpen(true); }}
                          onFixHttp={() => fixHttpConfig(d.domain)}
                          onInstallSsl={() => installSsl(d.domain)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </Card>
            )}
          </TabsContent>

          {/* ======= TAB: BULK ACTIONS ======= */}
          <TabsContent value="actions" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <ActionCard
                testId="action-scan"
                icon={Search}
                title="Tumunu Tara"
                desc="Tum domainlerin DNS, HTTP, HTTPS, SSL, Nginx, root path, sitemap ve robots durumunu tara."
                onClick={scanAll}
                loading={scanning}
                color="text-blue-400"
              />
              <ActionCard
                testId="action-nginx-test"
                icon={Terminal}
                title="Nginx Test"
                desc="nginx -t komutuyla mevcut yapilandirmayi dogrula."
                onClick={runNginxTest}
                loading={actionLoading.nginx_test}
                color="text-cyan-400"
              />
              <ActionCard
                testId="action-nginx-reload"
                icon={RotateCw}
                title="Nginx Reload"
                desc="Nginx yapilandirmasini yeniden yukle. Sadece test basariliysa calisir."
                onClick={runNginxReload}
                loading={actionLoading.nginx_reload}
                color="text-teal-400"
              />
              <ActionCard
                testId="action-quarantine"
                icon={ShieldX}
                title="Bozuk Config Karantina"
                desc="Eksik SSL sertifika dosyalarina referans veren nginx config dosyalarini izole et."
                onClick={runQuarantine}
                loading={actionLoading.quarantine}
                color="text-amber-400"
                danger
              />
              <ActionCard
                testId="action-bulk-fix"
                icon={Wrench}
                title="Toplu Otomatik Onarim"
                desc="Karantina + HTTP config duzeltme + SSL kurulum — tum domainler icin."
                onClick={runBulkAutoFix}
                loading={actionLoading.bulk_fix}
                color="text-red-400"
                danger
              />
            </div>

            {/* Safety Notes */}
            <Card className="bg-[hsl(var(--card))] border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Guvenlik Onlemleri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Her aksiyon oncesi onay dialog gosterilir (dry-run / confirm)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Nginx reload sadece nginx -t basariliysa calisir
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Root path asla silinmez — sadece dosya ekleme/guncelleme yapilir
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Mevcut nginx config degistirilmeden once backup alinir
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Certbot rate-limit korumasi: domain basina 5 dk cooldown
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    Tum islemler repair_logs koleksiyonuna kaydedilir
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ======= TAB: LOGS ======= */}
          <TabsContent value="logs" className="space-y-4">
            {/* Log Stats */}
            {logStats && (
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
                  <p className="text-xs text-muted-foreground">Toplam Islem</p>
                  <p className="text-xl font-semibold mt-1">{logStats.total_actions || 0}</p>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
                  <p className="text-xs text-muted-foreground">Basarili</p>
                  <p className="text-xl font-semibold mt-1 text-emerald-400">{logStats.ok || 0}</p>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
                  <p className="text-xs text-muted-foreground">Hatali</p>
                  <p className="text-xl font-semibold mt-1 text-red-400">{logStats.errors || 0}</p>
                </Card>
              </div>
            )}

            {/* Filter */}
            <div className="flex gap-3">
              <Select value={logFilter} onValueChange={setLogFilter}>
                <SelectTrigger data-testid="log-filter-select" className="w-[200px] bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Islem Filtresi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Islemler</SelectItem>
                  <SelectItem value="scan_all">Tarama</SelectItem>
                  <SelectItem value="fix_http_config">HTTP Config Fix</SelectItem>
                  <SelectItem value="install_ssl">SSL Kurulum</SelectItem>
                  <SelectItem value="nginx_test">Nginx Test</SelectItem>
                  <SelectItem value="nginx_reload">Nginx Reload</SelectItem>
                  <SelectItem value="quarantine">Karantina</SelectItem>
                  <SelectItem value="bulk_auto_fix">Toplu Onarim</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadLogs} className="border-border/50">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
              </Button>
            </div>

            {/* Log Table */}
            <Card className="bg-[hsl(var(--card))] border-border/70 overflow-hidden">
              <ScrollArea className="w-full max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Tarih</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Domain</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Islem</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Durum</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Sure</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Admin</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Detay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Henuz islem gecmisi yok
                        </TableCell>
                      </TableRow>
                    ) : logs.map(log => (
                      <TableRow key={log.id} className="border-border/30 hover:bg-secondary/20">
                        <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                          {fmtDate(log.created_at)}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{log.domain}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] border-border/50">
                            {log.action}
                          </Badge>
                          {log.dry_run && (
                            <Badge variant="outline" className="text-[10px] ml-1 border-amber-500/30 text-amber-400">DRY</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${log.status === 'ok' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-red-500/15 text-red-400 border-red-500/30'}`}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.duration_ms ? `${log.duration_ms}ms` : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{log.admin_email || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                          {log.stdout || log.stderr || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ======= DOMAIN DETAIL DIALOG ======= */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl bg-[hsl(var(--card))] border-border/70">
            <DialogHeader>
              <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                <Globe className="w-5 h-5" />
                {selectedDomain?.domain}
              </DialogTitle>
              <DialogDescription>
                Son tarama: {fmtDate(selectedDomain?.scanned_at)}
              </DialogDescription>
            </DialogHeader>
            {selectedDomain && (
              <div className="space-y-4">
                {/* Score */}
                <div className="flex items-center gap-4">
                  <div className={`text-4xl font-bold ${scoreColor(selectedDomain.score || 0)}`}>
                    {selectedDomain.score || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">/ 100 Saglik Skoru</div>
                </div>
                <Progress value={selectedDomain.score || 0} className="h-2" />

                {/* Score Breakdown */}
                {selectedDomain.score_breakdown && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(selectedDomain.score_breakdown).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between bg-secondary/30 rounded-lg px-3 py-2">
                        <span className="text-xs text-muted-foreground capitalize">{key}</span>
                        <span className={`text-sm font-medium ${val > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {val > 0 ? `+${val}` : val}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Alerts */}
                {selectedDomain.alerts && selectedDomain.alerts.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Uyarilar</h4>
                    {selectedDomain.alerts.map((alert, i) => (
                      <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
                        alert.level === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'
                      }`}>
                        {alert.level === 'critical'
                          ? <ShieldX className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                          : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                        }
                        <div>
                          <span className={`font-medium ${alert.level === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                            {alert.code}
                          </span>
                          <span className="text-muted-foreground ml-2">{alert.message}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Technical Details */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <DetailRow label="DNS A IP" value={selectedDomain.dns_root_ip} ok={selectedDomain.dns_ok} />
                  <DetailRow label="DNS WWW IP" value={selectedDomain.dns_www_ip} />
                  <DetailRow label="HTTP Status" value={selectedDomain.http_status} ok={[200, 301, 302].includes(selectedDomain.http_status)} />
                  <DetailRow label="HTTPS Status" value={selectedDomain.https_status} ok={[200, 301, 302].includes(selectedDomain.https_status)} />
                  <DetailRow label="SSL Subject" value={selectedDomain.ssl_subject || '—'} ok={selectedDomain.ssl_valid && !selectedDomain.wrong_cert} />
                  <DetailRow label="SSL Issuer" value={selectedDomain.ssl_issuer || '—'} />
                  <DetailRow label="SSL Expiry" value={selectedDomain.ssl_expiry_days >= 0 ? `${selectedDomain.ssl_expiry_days} gun` : 'Bilinmiyor'} ok={selectedDomain.ssl_expiry_days > 15} />
                  <DetailRow label="Nginx Config" value={selectedDomain.nginx_config_exists ? 'Mevcut' : 'Yok'} ok={selectedDomain.nginx_config_exists} />
                  <DetailRow label="Nginx Enabled" value={selectedDomain.nginx_enabled ? 'Aktif' : 'Pasif'} ok={selectedDomain.nginx_enabled} />
                  <DetailRow label="Root Path" value={selectedDomain.root_exists ? 'Mevcut' : 'Yok'} ok={selectedDomain.root_exists} />
                  <DetailRow label="index.html" value={selectedDomain.index_exists ? `${selectedDomain.index_size || 0} bytes` : 'Yok'} ok={selectedDomain.index_exists} />
                  <DetailRow label="sitemap.xml" value={selectedDomain.sitemap_exists ? 'Mevcut' : 'Yok'} ok={selectedDomain.sitemap_exists} />
                  <DetailRow label="robots.txt" value={selectedDomain.robots_exists ? 'Mevcut' : 'Yok'} ok={selectedDomain.robots_exists} />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fixHttpConfig(selectedDomain?.domain); setDetailOpen(false); }}
                className="border-border/50"
                disabled={actionLoading[`fix_${selectedDomain?.domain}`]}
              >
                <Wrench className="w-3.5 h-3.5 mr-1" /> HTTP Fix
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { installSsl(selectedDomain?.domain); setDetailOpen(false); }}
                className="border-border/50"
                disabled={actionLoading[`ssl_${selectedDomain?.domain}`]}
              >
                <Lock className="w-3.5 h-3.5 mr-1" /> SSL Kur
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ======= CONFIRM DIALOG ======= */}
        <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
          <DialogContent className="bg-[hsl(var(--card))] border-border/70">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                {confirmDialog?.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {confirmDialog?.description}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog(null)} className="border-border/50">
                Iptal
              </Button>
              <Button
                data-testid="confirm-action-btn"
                onClick={executeConfirmedAction}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
              >
                Onayla ve Calistir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

/* ============ SUB-COMPONENTS ============ */

function KPICard({ testId, label, value, icon: Icon, color = 'text-foreground' }) {
  return (
    <Card data-testid={testId} className="bg-[hsl(var(--card))] border-border/70">
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-semibold mt-1 tabular-nums ${color}`}>{value}</p>
          </div>
          <Icon className={`w-8 h-8 ${color} opacity-30`} />
        </div>
      </CardContent>
    </Card>
  );
}

function SortableHeader({ field, label, current, dir, onSort }) {
  const active = current === field;
  return (
    <TableHead
      className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors duration-150"
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active ? (dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );
}

function DomainRow({ data: d, actionLoading, onDetail, onFixHttp, onInstallSsl }) {
  const sslExpiring = (d.ssl_expiry_days || 999) <= 15 && d.ssl_expiry_days >= 0;
  const hasCritical = (d.critical_count || 0) > 0;

  return (
    <TableRow
      data-testid={`domain-row-${d.domain}`}
      className={`border-border/30 hover:bg-secondary/20 transition-colors duration-150 ${
        hasCritical ? 'bg-red-500/5' : ''
      }`}
    >
      <TableCell className="font-mono text-xs">
        <button
          onClick={onDetail}
          className="text-foreground hover:text-[hsl(var(--primary))] transition-colors duration-150 text-left"
          data-testid={`domain-detail-btn-${d.domain}`}
        >
          {d.domain}
        </button>
      </TableCell>
      <TableCell><StatusDot ok={d.dns_ok} /></TableCell>
      <TableCell><StatusDot ok={[200, 301, 302].includes(d.http_status)} warning={d.http_status > 0} /></TableCell>
      <TableCell><StatusDot ok={[200, 301, 302].includes(d.https_status)} /></TableCell>
      <TableCell><StatusDot ok={d.ssl_valid && !d.wrong_cert} warning={d.ssl_valid && d.wrong_cert} /></TableCell>
      <TableCell>
        <span className={`text-xs font-mono ${
          sslExpiring ? 'text-red-400 font-medium' : d.ssl_expiry_days > 0 ? 'text-muted-foreground' : 'text-muted-foreground/50'
        }`}>
          {d.ssl_expiry_days >= 0 ? `${d.ssl_expiry_days}g` : '—'}
        </span>
      </TableCell>
      <TableCell><StatusDot ok={d.nginx_enabled} warning={d.nginx_config_exists && !d.nginx_enabled} /></TableCell>
      <TableCell><StatusDot ok={d.root_exists} /></TableCell>
      <TableCell><StatusDot ok={d.sitemap_exists} /></TableCell>
      <TableCell><StatusDot ok={d.robots_exists} /></TableCell>
      <TableCell>
        <span className={`inline-flex items-center justify-center w-10 h-7 rounded-md text-xs font-bold border ${scoreBg(d.score || 0)} ${scoreColor(d.score || 0)}`}>
          {d.score || 0}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 hover:bg-secondary/50"
                onClick={onDetail}
                data-testid={`view-detail-${d.domain}`}
              >
                <Eye className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Detay</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 hover:bg-secondary/50"
                onClick={onFixHttp}
                disabled={actionLoading[`fix_${d.domain}`]}
                data-testid={`fix-http-${d.domain}`}
              >
                <Wrench className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>HTTP Config Duzelt</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 hover:bg-secondary/50"
                onClick={onInstallSsl}
                disabled={actionLoading[`ssl_${d.domain}`]}
                data-testid={`install-ssl-${d.domain}`}
              >
                <Lock className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>SSL Kur</TooltipContent>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ActionCard({ testId, icon: Icon, title, desc, onClick, loading, color = 'text-foreground', danger = false }) {
  return (
    <Card data-testid={testId} className={`bg-[hsl(var(--card))] border-border/70 transition-all duration-200 hover:border-[color:rgba(212,175,55,0.35)] ${danger ? 'hover:border-red-500/30' : ''}`}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-secondary/50 ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium mb-1">{title}</h3>
            <p className="text-xs text-muted-foreground mb-3">{desc}</p>
            <Button
              size="sm"
              variant={danger ? 'destructive' : 'outline'}
              onClick={onClick}
              disabled={loading}
              className={danger ? '' : 'border-border/50'}
            >
              {loading ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
              {loading ? 'Calisiyor...' : 'Calistir'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between bg-secondary/20 rounded-lg px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 font-mono">
        {ok !== undefined && (ok ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />)}
        {String(value)}
      </span>
    </div>
  );
}
